import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PixCheckout } from "@/components/PixCheckout";
import { PromoBanner } from "@/components/PromoBanner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, Zap, Crown, ArrowLeft, Sparkles, Lock, Clock, Loader2, Gift } from "lucide-react";
import { brlShort, SUB_STATES } from "@/lib/money";

const waLink = (name) => `https://wa.me/5519994045522?text=${encodeURIComponent(`Olá! Tenho interesse no plano ${name} da FlowIA`)}`;

export default function Plans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [billing, setBilling] = useState(null);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [checkout, setCheckout] = useState(null);
  const [freeCode, setFreeCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const loadBilling = () => { if (user) api.get("/billing/status").then(({ data }) => setBilling(data)).catch(() => {}); };
  useEffect(() => { api.get("/public/plans").then(({ data }) => setData(data)).catch(() => toast.error("Não foi possível carregar os planos.")); }, []);
  useEffect(() => { loadBilling(); }, [user]); // eslint-disable-line
  useEffect(() => { if (location.state?.checkout && user && data) setCheckout({ plan: data.plans.find((p) => p.id === "starter") || data.plans[0], period: "monthly" }); }, [location.state, user, data]);
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("pago") !== "1" || !user) return;
    const order_nsu = q.get("order_nsu"), transaction_nsu = q.get("transaction_nsu") || "", slug = q.get("slug") || "";
    if (!order_nsu) return;
    api.get("/billing/verify", { params: { order_nsu, transaction_nsu, slug } }).then(({ data: d }) => {
      setBilling((b) => ({ ...(b || {}), ...d }));
      if (d.plan_status === "active") toast.success("Pagamento confirmado! Plano ativo. 🎉");
      else toast.info("Status da confirmação do pagamento: aguardando a InfinitePay. Esta tela atualiza sozinha.");
    }).catch(() => toast.error("Não foi possível verificar o pagamento agora."));
    navigate("/planos", { replace: true });
  }, [location.search, user]); // eslint-disable-line

  const redeemFree = async () => {
    setRedeeming(true);
    try { const { data: r } = await api.post("/billing/coupon/free-period", { code: freeCode }); toast.success(`+${r.days} dias grátis adicionados! 🎁`); setFreeCode(""); loadBilling(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cupom inválido."); } finally { setRedeeming(false); }
  };

  const contratar = (plan) => { if (!user) { navigate("/login?modo=cadastro"); return; } setCheckout({ plan, period }); };
  const active = billing?.plan_status === "active";
  const hasAnnual = data?.plans?.some((p) => p.annual_price);
  const promos = data?.promotions || [];
  const freePromo = promos.find((p) => p.type === "free_period");

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-cyan-500/15 blur-[130px]" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-blue-600/15 blur-[130px]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 py-10 sm:py-16">
        <div className="mb-4 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300" data-testid="plans-back-link"><ArrowLeft className="h-4 w-4" /> Voltar</Link>
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-cyan-400 text-slate-950"><Zap className="h-4 w-4" strokeWidth={2.6} /></div>
            <span className="font-display text-lg font-extrabold text-white">Flow<span className="text-cyan-400">IA</span></span>
          </div>
        </div>

        <div className="mx-auto mb-10 max-w-2xl text-center">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1 text-xs font-mono-flow uppercase tracking-widest text-cyan-300"><Sparkles className="h-3.5 w-3.5" /> Planos & Preços</p>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Escolha o plano da sua empresa</h1>
          <p className="mt-4 text-slate-400">Comece com o Starter — totalmente funcional. Os demais chegam em breve com recursos avançados.</p>
          {billing?.blocked && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-200" data-testid="plans-blocked-banner">
              <Lock className="h-4 w-4" /> {billing.plan_status === "canceled" || billing.plan_status === "suspended" ? `Assinatura ${SUB_STATES[billing.plan_status].toLowerCase()}. Fale com o suporte para regularizar.` : "Seu teste grátis terminou. Assine para continuar usando a FlowIA."}
            </div>
          )}
          {billing && !billing.blocked && billing.plan_status === "trial" && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200" data-testid="plans-trial-banner">
              <Clock className="h-4 w-4" /> Teste grátis: {billing.days_left} dia(s) restante(s).
            </div>
          )}
          {billing?.pending_payment && billing.plan_status !== "active" && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-2 text-xs text-slate-300" data-testid="plans-pending-status">
              Status da confirmação do pagamento: <b>{billing.payment_states?.[billing.pending_payment.status]}</b>
            </div>
          )}
        </div>

        <div className="mx-auto mb-6 max-w-3xl space-y-4">
          <PromoBanner promotions={promos} />
          {hasAnnual && (
            <div className="flex justify-center">
              <div className="inline-flex rounded-full border border-slate-800 bg-slate-900/70 p-1" data-testid="period-toggle">
                {[["monthly", "Mensal"], ["annual", "Anual"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setPeriod(k)} data-testid={`period-${k}`}
                    className={`rounded-full px-5 py-1.5 text-sm font-medium transition-colors ${period === k ? "bg-cyan-400 text-slate-950" : "text-slate-400 hover:text-white"}`}>
                    {l}{k === "annual" && <span className="ml-1.5 text-xs opacity-80">economize</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {!data ? <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div> : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="plans-grid">
            {data.plans.map((p) => {
              const isAnnual = period === "annual" && p.annual_price;
              const price = p.custom_price ? "Sob consulta" : isAnnual ? brlShort(p.annual_price) : brlShort(p.price);
              const isCurrent = active && billing?.plan === p.id;
              return (
                <div key={p.id} data-testid={`plan-card-${p.id}`}
                  className={`relative flex flex-col rounded-2xl border p-6 transition-all ${p.popular ? "border-cyan-400/60 bg-slate-900/80 cyan-glow" : "glow-card"}`}>
                  {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-cyan-400 px-3 py-0.5 text-[11px] font-bold text-slate-950">MAIS POPULAR</span>}
                  <div className="flex items-center gap-2">
                    {p.id === "enterprise" && <Crown className="h-5 w-5 text-amber-400" />}
                    <h3 className="font-display text-xl font-bold text-white">{p.name}</h3>
                  </div>
                  {p.description && <p className="mt-1 text-xs text-slate-400">{p.description}</p>}
                  <div className="mt-3">
                    <span className="font-display text-3xl font-extrabold text-white" data-testid={`plan-price-${p.id}`}>{price}</span>
                    {!p.custom_price && <span className="text-sm text-slate-400">{isAnnual ? "/ano" : "/mês"}</span>}
                    {isAnnual && p.annual_discount_pct > 0 && <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">-{p.annual_discount_pct}%</span>}
                  </div>
                  <span className={`mt-3 inline-flex w-fit items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${p.available ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
                    {p.available ? "✅ Disponível" : "🔜 Em breve"}
                  </span>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-200"><Check className="h-4 w-4 shrink-0 text-cyan-400" /> {f}</li>
                    ))}
                    {p.limits?.ai_messages_month && <li className="flex items-center gap-2 text-xs text-slate-400"><Check className="h-3.5 w-3.5 shrink-0 text-slate-600" /> até {Number(p.limits.ai_messages_month).toLocaleString("pt-BR")} mensagens da IA/mês</li>}
                  </ul>
                  {p.available && !p.custom_price ? (
                    <Button onClick={() => contratar(p)} disabled={isCurrent || billing?.plan_status === "canceled" || billing?.plan_status === "suspended"} data-testid={`plan-btn-${p.id}`} className="mt-6 w-full bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300 disabled:opacity-80">
                      {!user ? `Começar teste grátis de ${data.trial_days} dias` : isCurrent ? "Plano atual ✓" : active ? "Trocar / renovar" : "Assinar agora"}
                    </Button>
                  ) : (
                    <a href={waLink(p.name)} target="_blank" rel="noopener noreferrer" className="mt-6">
                      <Button data-testid={`plan-btn-${p.id}`} className={`w-full font-semibold ${p.popular ? "bg-cyan-400 text-slate-950 hover:bg-cyan-300" : "bg-slate-800 text-cyan-300 hover:bg-slate-700"}`}>Falar com vendas</Button>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {user && billing && billing.plan_status !== "active" && (freePromo || true) && (
          <div className="mx-auto mt-10 max-w-md rounded-2xl border border-slate-800 bg-slate-900/50 p-4" data-testid="free-period-box">
            <p className="flex items-center gap-2 text-sm font-semibold text-white"><Gift className="h-4 w-4 text-amber-300" /> Tem um cupom de dias grátis?</p>
            <div className="mt-2 flex gap-2">
              <input value={freeCode} onChange={(e) => setFreeCode(e.target.value.toUpperCase())} placeholder="CÓDIGO" className="flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 font-mono-flow text-sm uppercase text-white outline-none focus:border-cyan-400" data-testid="free-period-input" />
              <Button onClick={redeemFree} disabled={!freeCode.trim() || redeeming} variant="outline" className="border-slate-700 bg-slate-900/50 text-cyan-300" data-testid="free-period-btn">{redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resgatar"}</Button>
            </div>
            <p className="mt-2 text-xs text-slate-500">Cupons de desconto em dinheiro são aplicados no checkout, ao assinar.</p>
          </div>
        )}
      </div>
      <PixCheckout open={!!checkout} onOpenChange={(o) => !o && setCheckout(null)} onActivated={loadBilling} plan={checkout?.plan} period={checkout?.period} promotions={promos} />
    </div>
  );
}
